<script lang="ts">
	/**
	 * The mini-player: fixed above the tab bar, present only while something is
	 * queued. Rendered once, in the root layout, so it survives navigation.
	 *
	 * It sits on `--tab-bar-block` (app.css: the content height `TabBar`
	 * measures and publishes, plus the bottom safe area) and in turn publishes
	 * its OWN height as `--mini-player`, which the reader's floating display
	 * button and the ayah action bar add to their offsets — so they stack and
	 * never overlap at any reading size. An in-flow spacer of the same height
	 * (`flex: none` in the shell's column) keeps the end of every page above it.
	 *
	 * It carries `view-transition-name: mini-player`, so a route change
	 * animates the page beneath it while the player stays put. Its card floats
	 * on `--shadow-2` and rises into place when a recitation starts
	 * (`@starting-style`); the buttons press through the global `.press` state
	 * layer.
	 */

	import { onMount } from 'svelte';

	import { RECITERS } from '$lib/audio/reciters';
	import type { PlayerState } from '$lib/audio/player';
	import Icon from '$lib/ui/icons/Icon.svelte';
	import { surahHref } from '$lib/ui/nav/links';

	import { recitationPlayer, trackLabel } from './player';

	const player = recitationPlayer();
	let current = $state<PlayerState>(player.state());
	let barEl = $state<HTMLElement | undefined>(undefined);

	onMount(() => player.subscribe((s) => (current = s)));

	let visible = $derived(current.track !== null);
	let playing = $derived(current.status === 'playing' || current.status === 'loading');

	/* Publish the bar's height while it is shown; clear it when it is not. */
	$effect(() => {
		const root = document.documentElement;
		const el = barEl;
		if (!visible || !el) {
			root.style.removeProperty('--mini-player');
			return;
		}
		const publish = () => {
			const h = el.getBoundingClientRect().height;
			if (h > 0) root.style.setProperty('--mini-player', `${h}px`);
		};
		publish();
		const observer = new ResizeObserver(publish);
		observer.observe(el);
		return () => {
			observer.disconnect();
			root.style.removeProperty('--mini-player');
		};
	});
</script>

{#if current.track}
	{@const track = current.track}
	<div class="spacer" aria-hidden="true"></div>
	<!--
		The measured box is the transparent `.mini` wrapper, and the card floats
		inside it: the wrapper's block-end padding is the gap above the tab bar,
		so `--mini-player` includes it and the reader's floating button and ayah
		bar, which add `--mini-player` to their offsets, clear the card by
		construction.
	-->
	<section class="mini" aria-label="مشغّل التلاوة" bind:this={barEl}>
		<div class="card">
			<div class="row">
				<div class="controls" role="group" aria-label="التحكم في التلاوة">
					<button type="button" class="press lift round" onclick={() => player.previous()}>
						<span class="flip"><Icon name="prev" /></span>
						<span class="visually-hidden">الآية السابقة</span>
					</button>
					<button type="button" class="main press lift round" onclick={() => player.toggle()}>
						{#if playing}
							<Icon name="pause" />
							<span class="visually-hidden">إيقاف مؤقت</span>
						{:else}
							<span class="flip"><Icon name="play" /></span>
							<span class="visually-hidden">تشغيل</span>
						{/if}
					</button>
					<button type="button" class="press lift round" onclick={() => player.next()}>
						<span class="flip"><Icon name="next" /></span>
						<span class="visually-hidden">الآية التالية</span>
					</button>
				</div>

				<a class="where press" href={surahHref(track.surah, track.ayah)}>
					{trackLabel(track)}
					{#if current.status === 'loading'}<span class="muted"> — جارٍ التحميل…</span>{/if}
				</a>

				<button type="button" class="stop press lift round" onclick={() => player.stop()}>
					<Icon name="stop" />
					<span class="visually-hidden">إيقاف التلاوة</span>
				</button>
			</div>

			<div class="row second">
				<label>
					<span class="visually-hidden">القارئ</span>
					<select
						value={current.reciterId}
						onchange={(event) => player.setReciter(event.currentTarget.value)}
					>
						{#each RECITERS as reciter (reciter.id)}
							<option value={reciter.id}>{reciter.name}</option>
						{/each}
					</select>
				</label>
				<span class="credit">EveryAyah.com</span>
			</div>

			{#if current.error}
				<p class="error" role="alert">{current.error}</p>
			{/if}
		</div>
	</section>
{/if}

<style>
	.spacer {
		flex: none;
		block-size: var(--mini-player, 6rem);
	}

	/* The measured, transparent band; see the markup. It lets taps through
	   everywhere except the card itself. */
	.mini {
		position: fixed;
		inset-inline: 0;
		inset-block-end: var(--tab-bar-block);
		z-index: 9;
		max-inline-size: var(--measure);
		margin-inline: auto;
		padding-block: 0 var(--space-2);
		padding-inline: var(--space-3);
		pointer-events: none;
		font-family: var(--font-ui);
		font-size: min(var(--step--1), 1.26rem);
		view-transition-name: mini-player;
	}

	/* A floating rounded card above the tab bar (D12). */
	.card {
		pointer-events: auto;
		padding: var(--space-2) var(--space-3);
		border-radius: var(--radius-lg);
		background-color: var(--bg-raised);
		box-shadow: var(--shadow-2);
		transition:
			opacity 240ms var(--ease-enter),
			translate 240ms var(--ease-enter);
	}

	/* Rises into place when a recitation starts; leaving is instant ({#if}). */
	@starting-style {
		.card {
			opacity: 0;
			translate: 0 1rem;
		}
	}

	.row {
		display: flex;
		align-items: center;
		gap: var(--space-2);
	}

	.second {
		margin-block-start: var(--space-1);
		justify-content: space-between;
	}

	.controls {
		display: flex;
		align-items: center;
		gap: 2px;
	}

	button {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		inline-size: 2.75rem;
		block-size: 2.75rem;
		padding: 0;
		color: var(--fg);
		background: none;
		border: 0;
		border-radius: var(--radius-pill);
		cursor: pointer;
		/* No `transition` and no `:hover` / `:active` of its own: `.press lift
		   round` (app.css) draws the tint and the 0.94 press, and a local
		   transition would override the lift's `scale` one. */
	}

	/* The one filled control in the player: play / pause. */
	button.main {
		color: var(--accent-fg);
		background: var(--accent);
	}

	button:focus-visible,
	.where:focus-visible,
	select:focus-visible {
		outline: var(--focus-ring-width) solid var(--accent);
		outline-offset: var(--focus-ring-offset);
	}

	.flip {
		display: inline-flex;
	}

	/* Media arrows point the way the text runs. */
	:global([dir='rtl']) .flip {
		transform: scaleX(-1);
	}

	/* As tall as the buttons beside it and rounded, so its press tint is a
	   wash over the row rather than a band hugging the text. */
	.where {
		flex: 1;
		min-inline-size: 0;
		padding-inline: var(--space-2);
		border-radius: var(--radius-md);
		line-height: 2.75rem;
		color: var(--fg);
		font-weight: 500;
		text-decoration: none;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.muted,
	.credit {
		color: var(--fg-muted);
		font-weight: 400;
	}

	.credit {
		font-size: var(--step--2);
		color: var(--fg-faint);
	}

	.stop {
		color: var(--fg-muted);
	}

	/* The reciter picker, compact: a sunken pill rather than a boxed field. */
	select {
		min-block-size: 2.75rem;
		max-inline-size: 70vw;
		padding-inline: var(--space-3);
		font-family: var(--font-ui);
		font-size: var(--step--2);
		color: var(--fg);
		background: var(--bg-sunken);
		border: 0;
		border-radius: var(--radius-pill);
	}

	.error {
		margin: var(--space-1) 0 0;
		font-size: var(--step--2);
		color: var(--danger);
	}
</style>
